import { itemsApi } from "#/api/itemsApi";
import { productApi } from "#/api/productApi";
import { ticketsApi } from "#/api/ticketsApi";
import { showSuccessNotification } from "#/components/notifications";
import Popover from "#/components/overlays/Popover";
import { TaskOptions } from "#/constants/taskOptions";
import type { CreateTicketPayload } from "#/types/tickets";
import {
  Button,
  Drawer,
  MultiSelect,
  Radio,
  RadioGroup,
  Select,
  Textarea,
  TextInput,
  type DrawerProps,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { IconChevronDown, IconSend2, IconUpload } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";

export interface Props extends DrawerProps {
  itemIds?: number[];
  onSuccess: () => void;
}

const CreateTicketDrawer = ({ itemIds, onSuccess, ...props }: Props) => {
  const { t } = useTranslation();
  const { canWrite } = usePermission();
  const [showSendOptions, setShowSendOptions] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [linkType, setLinkType] = useState<string>("");
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);

  const hasPreselectedItems = itemIds && itemIds.length > 0;

  // Fetch items for multi-select
  const { data: itemsData } = useQuery({
    queryKey: ["items-for-ticket"],
    queryFn: async () => {
      const response = await itemsApi.getAll({ limit: 100000 });
      return response.data.data;
    },
    enabled: !hasPreselectedItems && linkType === "item",
  });

  // Fetch products for multi-select
  const { data: productsData } = useQuery({
    queryKey: ["products-for-ticket"],
    queryFn: async () => {
      const response = await productApi.getAll({ limit: 100000 });
      return response.data.data;
    },
    enabled: !hasPreselectedItems && linkType === "category",
  });

  const form = useForm({
    initialValues: {
      task: "",
      deadline: "",
      notify_email: "",
      person_in_charge: "",
      submitType: "send",
      status: "todo",
      problem_details: "",
    },
    validate: {
      task: (value) => (value ? null : t("tickets.validation.taskRequired")),
      deadline: (value) => (value ? null : t("tickets.validation.deadlineRequired")),
      person_in_charge: (value) =>
        value ? null : t("tickets.validation.personInChargeRequired"),
      notify_email: (value) => {
        if (!value) return t("tickets.validation.notifyEmailRequired");
        if (!/^\S+@\S+\.\S+$/.test(value)) return t("tickets.validation.validEmail");
        return null;
      },
    },
    transformValues: (values) => ({
      ...values,
      deadline: values.deadline
        ? moment(values.deadline).format("YYYY-MM-DD")
        : "",
    }),
  });

  // Custom validation for link fields
  const validateLinkFields = () => {
    if (hasPreselectedItems) return true;
    if (!linkType) return false;
    if (linkType === "item" && selectedItems.length === 0) return false;
    if (linkType === "category" && selectedProducts.length === 0) return false;
    return true;
  };

  const handleSubmit = async (values: typeof form.values) => {
    // Validate link fields
    if (!validateLinkFields()) {
      return;
    }

    setSending(true);

    const { submitType, ...withoutType } = values;

    // Determine which items/products to send
    let itemsToSend: number[] = [];
    let productsToSend: number[] = [];

    if (hasPreselectedItems) {
      // Use pre-selected items from props (existing flow)
      itemsToSend = itemIds!;
    } else {
      // Use selected items/products from form (new flow)
      if (linkType === "item") {
        itemsToSend = selectedItems.map((id) => Number(id));
      } else if (linkType === "category") {
        productsToSend = selectedProducts.map((id) => Number(id));
      }
      // If linkType === "none", both arrays remain empty
    }

    const cleanedValues = {
      ...withoutType,
      items: itemsToSend.length > 0 ? itemsToSend : undefined,
      products: productsToSend.length > 0 ? productsToSend : undefined,
    };

    try {
      if (submitType === "sendAndExportPdf") {
        await ticketsApi.createAndExportPdf(
          cleanedValues as CreateTicketPayload
        );
      } else {
        await ticketsApi.create(cleanedValues as CreateTicketPayload);
      }
      onSuccess();
      setSending(false);
      showSuccessNotification({
        message:
          submitType === "sendAndExportPdf"
            ? t("tickets.createdAndExportedSuccess")
            : t("tickets.createdSuccess"),
      });
      resetForm();
    } catch (error) {
      console.error("🚀 ~ handleSubmit ~ error:", error);
      setSending(false);
    }
  };

  const resetForm = () => {
    form.reset();
    setLinkType("");
    setSelectedItems([]);
    setSelectedProducts([]);
  };

  return (
    <Drawer
      {...props}
      position={"right"}
      title={t("tickets.createTicket")}
      closeOnClickOutside={false}
      size={"lg"}
      onClose={() => {
        resetForm();
        props.onClose();
      }}
    >
      <form className="space-y-4" onSubmit={form.onSubmit(handleSubmit)}>
        {!hasPreselectedItems && (
          <>
            <Select
              label={t("tickets.form.whatIsLinkedTo")}
              required
              placeholder={t("tickets.form.selectLinkType")}
              data={[
                { label: t("tickets.form.item"), value: "item" },
                { label: t("tickets.form.category"), value: "category" },
                { label: t("tickets.form.none"), value: "none" },
              ]}
              value={linkType}
              onChange={(value) => {
                setLinkType(value || "");
                setSelectedItems([]);
                setSelectedProducts([]);
              }}
            />

            {linkType === "item" && (
              <MultiSelect
                label={t("tickets.form.selectItems")}
                required
                placeholder={t("tickets.form.selectItems")}
                data={
                  itemsData?.map((item) => ({
                    label: `Item #${item.id}`,
                    value: String(item.id),
                  })) || []
                }
                value={selectedItems}
                onChange={setSelectedItems}
                searchable
              />
            )}

            {linkType === "category" && (
              <MultiSelect
                label={t("tickets.form.selectCategories")}
                required
                placeholder={t("tickets.form.selectCategories")}
                data={
                  productsData?.map((product) => ({
                    label: product.name,
                    value: String(product.id),
                  })) || []
                }
                value={selectedProducts}
                onChange={setSelectedProducts}
                searchable
              />
            )}
          </>
        )}

        <Select
          label={t("tickets.form.task")}
          required
          placeholder={t("tickets.form.taskPlaceholder")}
          data={TaskOptions.map((opt) => ({ ...opt, label: t(opt.label) }))}
          {...form.getInputProps("task")}
        />

        <DateInput
          label={t("tickets.form.deadlineDate")}
          required
          placeholder={t("tickets.form.deadlinePlaceholder")}
          minDate={new Date()}
          {...form.getInputProps("deadline")}
        />

        <TextInput
          label={t("tickets.form.notifyInformation")}
          required
          placeholder={t("tickets.form.notifyEmailPlaceholder")}
          type="email"
          {...form.getInputProps("notify_email")}
        />

        <TextInput
          label={t("tickets.form.personInCharge")}
          required
          placeholder={t("tickets.form.personInChargePlaceholder")}
          {...form.getInputProps("person_in_charge")}
        />

        <RadioGroup
          label={t("tickets.form.markAs")}
          required
          variant="horizonal"
          {...form.getInputProps("status")}
        >
          <Radio label={t("tickets.form.orderToPerform")} value="todo" />
          <Radio label={t("tickets.form.orderInProgress")} value="in_progress" />
          <Radio label={t("tickets.form.problem")} value="problem" />
        </RadioGroup>

        {form.values.status === "problem" && (
          <Textarea
            label={t("tickets.form.problemDetails")}
            required
            classNames={{
              input: "h-24",
            }}
            placeholder={t("tickets.form.problemPlaceholder")}
            {...form.getInputProps("problem_details")}
          />
        )}

        <div className="w-full grid grid-cols-2 gap-4">
          <Button
            color="black"
            variant="outline"
            type="reset"
            onClick={resetForm}
          >
            {t("common.clear")}
          </Button>
          <Popover
            opened={showSendOptions}
            onChange={setShowSendOptions}
            classNames={{
              overlay: "backdrop-blue-none",
            }}
            width={250}
            anchor={
              <PermissionTooltip disabled={!canWrite()}>
                <Button
                  color="black"
                  variant="filled"
                  rightSection={<IconChevronDown size={20} />}
                  onClick={() => setShowSendOptions(true)}
                  loading={sending}
                  disabled={!canWrite()}
                >
                  {t("common.send")}
                </Button>
              </PermissionTooltip>
            }
          >
            <div className="flex flex-col gap-2">
              <PermissionTooltip disabled={!canWrite()}>
                <Button
                  color="black"
                  variant="filled"
                  leftSection={<IconSend2 size={20} />}
                  fullWidth
                  type="button"
                  disabled={!canWrite()}
                  onClick={() => {
                    setShowSendOptions(false);
                    form.setFieldValue("submitType", "send");
                    form.onSubmit(handleSubmit)();
                  }}
                >
                  {t("common.send")}
                </Button>
              </PermissionTooltip>

              <PermissionTooltip disabled={!canWrite()}>
                <Button
                  color="black"
                  variant="outline"
                  leftSection={<IconUpload size={20} />}
                  fullWidth
                  type="button"
                  disabled={!canWrite()}
                  onClick={() => {
                    setShowSendOptions(false);
                    form.setFieldValue("submitType", "sendAndExportPdf");
                    form.onSubmit(handleSubmit)();
                  }}
                >
                  {t("tickets.form.sendAndExportPdf")}
                </Button>
              </PermissionTooltip>
            </div>
          </Popover>
        </div>
      </form>
    </Drawer>
  );
};

export default CreateTicketDrawer;
